import * as Sentry from '@sentry/react';
import { IConfig } from 'packages/config';
import { ILogger } from './libs/interfaces/interfaces';
import { ANSIColor } from './libs/enums/enums';
import { LogLevel } from './libs/types/types';

type Options = {
  prefix?: string;
  color?: ANSIColor;
};

class Logger implements ILogger {
  private appConfig: IConfig;
  private prefix: string;

  public constructor(appConfig: IConfig, opts?: Options) {
    this.appConfig = appConfig;
    this.prefix = opts?.prefix ? this.formatPrefix(opts.prefix, opts.color) : '';
  }

  public addPrefix(prefix: string, color: ANSIColor = ANSIColor.BrightGreen): Logger {
    return new Logger(this.appConfig, { prefix, color });
  }

  public debug(summary: string, ...details: unknown[]): void {
    this.log('debug', summary, ...details);
  }

  public info(summary: string, ...details: unknown[]): void {
    this.log('info', summary, ...details);
  }

  public warn(summary: string, ...details: unknown[]): void {
    this.log('warn', summary, ...details);
  }

  public error(summary: string, ...details: unknown[]): void {
    this.log('error', summary, ...details);
    Sentry.captureMessage(summary, {
      level: 'error',
      extra: { details },
    });
  }

  private log(level: LogLevel, summary: string, ...details: unknown[]): void {
    const timestamp = new Date().toISOString();
    const formattedMessage = `${ANSIColor.Dim}[${timestamp}]${ANSIColor.Reset}${this.prefix} ${summary}`;

    switch (level) {
      case 'debug':
        console.debug(formattedMessage, ...details);
        break;
      case 'info':
        console.info(formattedMessage, ...details);
        break;
      case 'warn':
        console.warn(formattedMessage, ...details);
        break;
      case 'error':
        console.error(formattedMessage, ...details);
        break;
    }
  }

  private formatPrefix(prefix: string, color: ANSIColor = ANSIColor.BrightGreen): string {
    return ` ${color}[${prefix}]${ANSIColor.Reset}`;
  }
}

export { Logger };
